import { useState } from "react";
import { List, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useWorkspaceStore } from "@/store/workspace-store";
import { dealViewsApi, type DealView } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";

/**
 * Bulk "add to list".
 *
 * Only curated views are offered. A filter view's contents are defined by its
 * conditions, so adding to one would be silently discarded on the next read —
 * the server refuses it, and offering it here would be a lie.
 */
export function AddToListMenu({
  selectedDealIds,
  workspaceId,
  onDone,
}: {
  /** Company ids — what the table's selection holds. */
  selectedDealIds: string[];
  workspaceId: string;
  onDone?: () => void;
}) {
  const { activeWorkspace, fetchWorkspaces } = useWorkspaceStore();
  const [isSaving, setIsSaving] = useState(false);

  const lists = (
    ((activeWorkspace as { deal_views?: DealView[] } | undefined)?.deal_views ??
      []) as DealView[]
  )
    .filter((view) => view.mode === "list")
    .sort((a, b) => a.order - b.order);

  const addTo = async (view: DealView) => {
    setIsSaving(true);
    try {
      const result = await dealViewsApi.setMembership(
        workspaceId,
        view._id,
        selectedDealIds
      );
      toast.success(
        `${result.modified} ${result.modified === 1 ? "deal" : "deals"} added to ${view.name}`
      );
      onDone?.();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't add to the list"));
    } finally {
      setIsSaving(false);
    }
  };

  const createAndAdd = async () => {
    const name = window.prompt("Name for the new list");
    if (!name?.trim()) return;

    setIsSaving(true);
    try {
      const existing = (
        ((activeWorkspace as { deal_views?: DealView[] } | undefined)
          ?.deal_views ?? []) as DealView[]
      ).map((view) => ({ ...view }));

      const saved = await dealViewsApi.save(workspaceId, [
        ...existing,
        { name: name.trim(), order: existing.length, mode: "list" },
      ]);

      // The server assigns the id, so the new list has to be found in what it
      // returned rather than guessed at.
      const created = saved.find(
        (view) => view.name === name.trim() && view.mode === "list"
      );
      if (!created) throw new Error("The list was not created");

      await dealViewsApi.setMembership(workspaceId, created._id, selectedDealIds);
      await fetchWorkspaces();
      toast.success(`Added to new list "${created.name}"`);
      onDone?.();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't create the list"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          disabled={isSaving || selectedDealIds.length === 0}
        >
          <List className="h-3.5 w-3.5" />
          Add to list
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {selectedDealIds.length} selected
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {lists.length === 0 && (
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            No lists yet
          </DropdownMenuLabel>
        )}

        {lists.map((view) => (
          <DropdownMenuItem key={view._id} onSelect={() => addTo(view)}>
            <List className="mr-2 h-3.5 w-3.5" />
            {view.name}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={createAndAdd}>
          <Plus className="mr-2 h-3.5 w-3.5" />
          New list…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
